import { useState } from 'react'
import {
  useUpdatePositionRole,
  usePositionsForSettings,
  type PositionMini,
} from '@/lib/useProfitSettings'
import { RoleMoveModal } from './RoleMoveModal'
import type { ProfitRole } from '@/types/profitSettings'

const GROUP_DEFS: { id: ProfitRole; label: string; sub: string }[] = [
  { id: 'core', label: 'Core', sub: 'Hold-Forever · keine Profit-Trigger' },
  { id: 'satellite', label: 'Satellite', sub: 'Volle Trigger (Default)' },
  { id: 'single_stock', label: 'Single Stock', sub: 'Trigger nur wenn steuerfrei' },
]

export function RolesGroupedList() {
  const query = usePositionsForSettings()
  const mutation = useUpdatePositionRole()
  const [movingId, setMovingId] = useState<number | null>(null)

  if (query.isLoading) {
    return <p className="font-mono text-sm text-muted">Lade Positionen …</p>
  }
  if (query.isError) {
    return (
      <p role="alert" className="font-mono text-sm text-loss">
        Positionen konnten nicht geladen werden.
      </p>
    )
  }

  const positions: PositionMini[] = query.data ?? []
  if (positions.length === 0) {
    return (
      <div className="border border-hairline bg-panel px-6 py-12 text-center">
        <p className="font-mono text-sm text-muted">
          Keine Positionen gefunden. Lege erst Positionen unter /portfolio an.
        </p>
      </div>
    )
  }

  const moving = positions.find((p) => p.id === movingId) ?? null

  return (
    <div className="border border-hairline bg-panel px-6 py-6">
      {GROUP_DEFS.map((g) => {
        const members = positions.filter((p) => p.profit_role === g.id)
        return (
          <div key={g.id} className="mt-4 first:mt-0">
            <div className="flex items-baseline justify-between border-b border-hairline py-2">
              <h4 className="tracked-caps text-[10px] text-ink">
                {g.label} · {members.length}
              </h4>
              <p className="font-mono text-xs text-muted">{g.sub}</p>
            </div>
            {members.length === 0 ? (
              <p className="py-2 font-mono text-xs text-faint">— keine Positionen —</p>
            ) : (
              members.map((p) => (
                <div
                  key={p.id}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-hairline py-2"
                >
                  <div>
                    <p className="text-sm text-ink">{p.name}</p>
                    <p className="font-mono text-xs text-muted">{p.isin}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMovingId(p.id)}
                    className="font-mono text-xs text-bernstein hover:underline"
                  >
                    → verschieben
                  </button>
                </div>
              ))
            )}
          </div>
        )
      })}

      <RoleMoveModal
        open={moving !== null}
        positionName={moving?.name ?? ''}
        currentRole={(moving?.profit_role ?? 'satellite') as ProfitRole}
        isPending={mutation.isPending}
        onClose={() => setMovingId(null)}
        onSelect={(role) => {
          if (moving === null) return
          mutation.mutate({ positionId: moving.id, role }, { onSuccess: () => setMovingId(null) })
        }}
      />
    </div>
  )
}
